import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { observer } from 'mobx-react'
import React, { useEffect, useMemo, useState } from 'react'

import { Button, Col, Modal, Row } from 'antd'
import { BREAK_POINTS_NUMBERS } from 'flynk.app.web.core.components/constants'
import { useWindowSize } from 'flynk.app.web.core.components/helpers'
import PropTypes from 'prop-types'

import { ModalTitle } from '../../components/QuoteBuilder/ModalTitle'
import { CeremonyMusicGroupModal } from '../../components/QuoteBuilder/PackageDetails/CeremonyMusicGroupModal'
import { CeremonyMusicGroupTab } from '../../components/QuoteBuilder/PackageDetails/CeremonyMusicGroupTab'
import CeremonyMusicModalItem from '../../components/QuoteBuilder/PackageDetails/CeremonyMusicModalItem'
import {
  GIG_LABEL,
  GIG_TYPES,
  NO_CEREMONY,
  NO_PERFORMANCE,
  PERFORMANCE_GROUPS,
  QUOTE_BUILDER_BREAKPOINT,
} from '../../constants'
// import Testimonials from '../../components/QuoteBuilder/Testimonials'
import {
  conditionedClassName,
  postAMessageFromIframeToParent,
  useStores,
} from '../../helpers/common'

const CeremonyMusicModalContainer = ({ wrapClassName }) => {
  const {
    bookingStore: {
      isPrivateEventBooking,
      ceremonyMusicGroupModalVisible,
      ceremonyPerformances,
      getPerformances,
      selectCeremonyPerformance,
      selectCeremonyPerformanceLoading,
      selectedCeremonyPerformance,
      toggleCeremonyMusicGroupModalVisible,
      toggleCeremonyMusicModalVisible,
      pauseOtherVideos,
      booking,
    },
  } = useStores()
  const [selectedGroup, setSelectedGroup] = useState(PERFORMANCE_GROUPS.harp)
  const [ceremonyPerformancesByGroup, setCeremonyPerformancesByGroup] =
    useState([])

  const { windowInnerHeight } = useWindowSize(QUOTE_BUILDER_BREAKPOINT)
  const { isNarrow } = useWindowSize(BREAK_POINTS_NUMBERS.xl)

  const activeClass = 'active'

  const title = useMemo(() => {
    if (isPrivateEventBooking) {
      return GIG_LABEL.Ceremony.privateLabel
    }

    if (isNarrow) {
      return GIG_TYPES.Ceremony
    }

    return GIG_LABEL.Ceremony.label
  }, [isPrivateEventBooking, isNarrow])

  const noPerformanceLabel = isPrivateEventBooking
    ? NO_PERFORMANCE.Ceremony.privateLabel
    : NO_PERFORMANCE.Ceremony.label

  useEffect(() => {
    if (ceremonyPerformances.length === 0) {
      getPerformances()
    }
  })

  const closeButton = (
    <Button key={1} type="text" onClick={toggleCeremonyMusicModalVisible}>
      <FontAwesomeIcon icon={['far', 'times']} className="font-awesome-icon" />
    </Button>
  )

  const buttons = [closeButton]

  const handleCancelCallBack = () => {
    setSelectedGroup(PERFORMANCE_GROUPS.harp)
    setCeremonyPerformancesByGroup([])
  }

  const onArrowClick = group => {
    setSelectedGroup(group)
    setCeremonyPerformancesByGroup(
      ceremonyPerformances.filter(performance => performance.group === group)
    )
    toggleCeremonyMusicGroupModalVisible()
  }

  const onTabClick = group => {
    setSelectedGroup(group)
    pauseOtherVideos()
  }

  const onSelectItemClick = id => {
    selectCeremonyPerformance(id)
    toggleCeremonyMusicModalVisible()
    postAMessageFromIframeToParent('IsSelectedItemOnCeremonyPage', true)
  }

  const renderNoGroup = () => (
    <Row
      className={`band-modal-content-ceremony-list ${selectedGroup === PERFORMANCE_GROUPS.noCeremony ? activeClass : ''}`}
      gutter={[20, 50]}
    >
      <div className="band-modal-content-no-cat no-cat">
        <p className="band-modal-content-no-cat__title no-cat__title">
          {noPerformanceLabel}
        </p>
        <p className="band-modal-content-no-cat__des">
          Select this option if you don&apos;t need us for your{' '}
          {title?.toLowerCase()}.
        </p>
        <Button
          type="primary"
          disabled={selectCeremonyPerformanceLoading}
          className={conditionedClassName({
            'qb-button qb-button-change': true,
            selected: selectedCeremonyPerformance.id === NO_CEREMONY,
          })}
          ghost={selectedCeremonyPerformance.id === NO_CEREMONY}
          onClick={() =>
            selectedCeremonyPerformance.id !== NO_CEREMONY &&
            onSelectItemClick(NO_CEREMONY)
          }
        >
          {selectedCeremonyPerformance.id === NO_CEREMONY
            ? 'Selected'
            : 'Select'}
        </Button>
      </div>
    </Row>
  )

  const renderPerformanceGroup = groupId => {
    const content = ceremonyPerformances?.reduce((result, performance) => {
      if (performance.group === groupId) {
        result.push(
          <Col xs={6} key={performance.id}>
            <CeremonyMusicModalItem
              performance={performance}
              selectCeremonyPerformance={onSelectItemClick}
              selectCeremonyPerformanceLoading={
                selectCeremonyPerformanceLoading
              }
              booking={booking}
            />
          </Col>
        )
      }

      return result
    }, [])

    return (
      <Row
        className={`band-modal-content-ceremony-list ${selectedGroup === groupId ? activeClass : ''}`}
        gutter={[20, 50]}
        key={groupId}
      >
        {content}
      </Row>
    )
  }

  return (
    <Modal
      centered
      open
      footer={null}
      closable={false}
      onCancel={toggleCeremonyMusicModalVisible}
      maskClosable
      wrapClassName={`${wrapClassName} qb-scroll`}
      className="qb-modal band-modal ceremony-modal"
      style={{ maxHeight: windowInnerHeight }}
      title={<ModalTitle buttons={buttons} title={title} />}
      getContainer="#qbDetails"
    >
      <div className="band-modal-content">
        <div className="band-modal-content-ceremony qb-scroll">
          <div className="band-modal-content-ceremony-group-wrapper desktop">
            <div className="band-modal-content-ceremony-group">
              <CeremonyMusicGroupTab
                onTabClick={onTabClick}
                group={PERFORMANCE_GROUPS.harp}
                thumbnail="/images/ceremony-harp.jpeg"
                hasCurSelectedItem={selectedGroup === PERFORMANCE_GROUPS.harp}
              />
              <CeremonyMusicGroupTab
                onTabClick={onTabClick}
                group={PERFORMANCE_GROUPS.acoustic}
                thumbnail="/images/ceremony-acoustic.jpeg"
                hasCurSelectedItem={
                  selectedGroup === PERFORMANCE_GROUPS.acoustic
                }
              />
              <CeremonyMusicGroupTab
                onTabClick={onTabClick}
                group={PERFORMANCE_GROUPS.string}
                thumbnail="/images/ceremony-string.jpeg"
                hasCurSelectedItem={selectedGroup === PERFORMANCE_GROUPS.string}
              />
              <CeremonyMusicGroupTab
                onTabClick={onTabClick}
                title={noPerformanceLabel}
                group={PERFORMANCE_GROUPS.noCeremony}
                noGroup
                hasCurSelectedItem={
                  selectedGroup === PERFORMANCE_GROUPS.noCeremony
                }
              />
            </div>
          </div>
          <div className="band-modal-content-ceremony-group mobile">
            <CeremonyMusicGroupTab
              onArrowClick={onArrowClick}
              group={PERFORMANCE_GROUPS.harp}
              thumbnail="/images/ceremony-harp.jpeg"
              hasCurSelectedItem={
                selectedCeremonyPerformance.group === PERFORMANCE_GROUPS.harp
              }
            />
            <CeremonyMusicGroupTab
              onArrowClick={onArrowClick}
              group={PERFORMANCE_GROUPS.acoustic}
              thumbnail="/images/ceremony-acoustic.jpeg"
              hasCurSelectedItem={
                selectedCeremonyPerformance.group ===
                PERFORMANCE_GROUPS.acoustic
              }
            />
            <CeremonyMusicGroupTab
              onArrowClick={onArrowClick}
              group={PERFORMANCE_GROUPS.string}
              thumbnail="/images/ceremony-string.jpeg"
              hasCurSelectedItem={
                selectedCeremonyPerformance.group === PERFORMANCE_GROUPS.string
              }
            />
            <button
              type="button"
              className="band-modal-content-ceremony-group-item-no-cat"
              onClick={() => onArrowClick(PERFORMANCE_GROUPS.noCeremony)}
            >
              <div className="band-modal-content-no-cat__icon-container">
                <div className="band-modal-content-no-cat__icon no-cat__icon">
                  <img src="/images/no-cat-icon.svg" alt="no-cat-icon" />
                </div>
              </div>
              <div className="band-modal-content-no-cat__text">
                <p className="band-modal-content-no-cat__title no-cat__title">
                  {noPerformanceLabel}
                </p>
                {selectedCeremonyPerformance.id === NO_CEREMONY ? (
                  <div className="band-modal-content-no-cat__selected-label">
                    <span>Selected</span>
                    <FontAwesomeIcon
                      icon={['fas', 'chevron-right']}
                      className="font-awesome-icon"
                    />
                  </div>
                ) : null}
              </div>
            </button>
          </div>
          <div className="band-modal-content-ceremony-list-wrapper qb-scroll-has-track-piece">
            {renderPerformanceGroup(PERFORMANCE_GROUPS.string)}
            {renderPerformanceGroup(PERFORMANCE_GROUPS.acoustic)}
            {renderPerformanceGroup(PERFORMANCE_GROUPS.harp)}
            {renderNoGroup()}
          </div>
        </div>
        {
          // !isNarrow &&
          // selectedGroup !== PERFORMANCE_GROUPS.noCeremony &&
          // <Testimonials />
        }
      </div>
      {ceremonyMusicGroupModalVisible && (
        <CeremonyMusicGroupModal
          booking={booking}
          group={selectedGroup}
          performances={ceremonyPerformancesByGroup}
          wrapClassName={wrapClassName}
          handleCancelCallBack={handleCancelCallBack}
          selectCeremonyPerformance={onSelectItemClick}
          toggleCeremonyMusicModalVisible={toggleCeremonyMusicModalVisible}
          toggleCeremonyMusicGroupModalVisible={
            toggleCeremonyMusicGroupModalVisible
          }
          selectCeremonyPerformanceLoading={selectCeremonyPerformanceLoading}
          selectedCeremonyPerformance={selectedCeremonyPerformance}
        />
      )}
    </Modal>
  )
}

CeremonyMusicModalContainer.propTypes = {
  wrapClassName: PropTypes.string,
}

CeremonyMusicModalContainer.defaultProps = {
  wrapClassName: '',
}

export default observer(CeremonyMusicModalContainer)
